import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { api, saveBlob } from "../api";
import { navigate } from "../router";
import type {
  Attachment, Citation, ContextInfo, Conversation, DraftRef, Message, StreamStep, User,
} from "../types";
import { AnswerCard, plainAnswer } from "./AnswerCard";
import { AttachButton, AttachmentChips, AttachmentList, useUploads } from "./Attachments";
import { DecisionPanel } from "./DecisionPanel";
import { DocumentPanel } from "./DocumentPanel";
import { DraftEditor } from "./DraftEditor";
import { IconDoc, IconMenu, IconSend } from "./Icons";
import { Shell } from "./Shell";
import { ConversationList } from "./Sidebar";
import { LiveSteps } from "./StepList";

const EXAMPLES = [
  "Poate angajatorul să concedieze un salariat aflat în concediu medical?",
  "Ce termen de prescripție se aplică acțiunii în anularea unui contract?",
  "Ce consecințe are pentru angajator o concediere fără temei legal?",
];

/** The thread as Markdown, for Exportă. Built here: it is what is on screen. */
function threadMarkdown(title: string, messages: Message[]): string {
  const out = [`# ${title}`, ""];
  for (const m of messages) {
    if (m.role === "user") out.push(`**Întrebare:** ${m.content}`, "");
    else if (!m.error) out.push(plainAnswer(m), "");
  }
  out.push(
    "---",
    "Exportat din ArhiLex. Răspunsurile citează legea în vigoare la data " +
      "întrebării; verificați sursa înainte de a vă baza pe ele.",
  );
  return out.join("\n");
}

/** An answer that did not arrive, shown where it would have been. */
function failed(err: unknown): Message {
  return {
    id: `err-${Date.now()}`,
    role: "assistant",
    content:
      typeof err === "string"
        ? err
        : err instanceof Error
          ? err.message
          : "A apărut o eroare.",
    created_at: new Date().toISOString(),
    error: true,
  };
}

/**
 * Cercetare: the conversation list in the sidebar, the thread, the composer,
 * and the source panel on the right.
 *
 * The conversation is in the URL (/chat/:id), so this component reads the
 * id from its prop and loads it, rather than holding the selection itself.
 */
export function Research({
  user,
  onLogout,
  conversationId,
}: {
  user: User;
  onLogout: () => void;
  conversationId: string | null;
}) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [context, setContext] = useState<ContextInfo | null>(null);
  const [draft, setDraft] = useState("");
  // Files attached to the question being typed. Emptied when it is sent -
  // they belong to that turn from then on.
  const [attached, setAttached] = useState<Attachment[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [liveSteps, setLiveSteps] = useState<StreamStep[]>([]);
  // True from the first render when the URL names a conversation, so a deep
  // link never flashes the "new conversation" screen before it loads.
  const [loading, setLoading] = useState(conversationId !== null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [drawer, setDrawer] = useState(false);
  /**
   * One panel, two things that can hold it: a source, or a draft the agent
   * produced. Mutually exclusive - two competing for it would either stack
   * or fight.
   */
  const [source, setSource] = useState<Citation | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [docMenu, setDocMenu] = useState(false);
  // Every document this thread made, newest last, for the bar's button.
  const threadDocs: DraftRef[] = [
    ...new Map(messages.flatMap((m) => m.drafts ?? []).map((d) => [d.id, d])).values(),
  ];
  const endRef = useRef<HTMLDivElement>(null);
  // Set when this component created the conversation the URL now names, so
  // the load effect does not refetch what is already on screen.
  const created = useRef<string | null>(null);
  // The thread on screen, for an answer that lands after the reader moved
  // to another one: it is stored either way, but must not be shown there.
  const shown = useRef<string | null>(null);
  shown.current = loadedId;

  const uploads = useUploads({
    attached,
    conversationId: loadedId,
    onChange: setAttached,
    onError: setUploadError,
  });

  const refreshList = useCallback(async () => {
    try {
      setConversations(await api.conversations());
    } catch {
      /* the list is a convenience; a failure must not block asking */
    }
  }, []);

  useEffect(() => {
    void refreshList();
  }, [refreshList]);

  function reset() {
    setLoadedId(null);
    setMessages([]);
    setContext(null);
    setDraft("");
    setAttached([]);
    setUploadError(null);
    setDraftId(null);
    setSource(null);
    setLoadError(null);
  }

  // Follow the URL. A file attached to an unsent question does not follow
  // the reader into another thread, and neither does the panel: a draft
  // from one conversation open beside another is a misattribution.
  useEffect(() => {
    if (conversationId === loadedId) {
      if (created.current === conversationId) created.current = null;
      return;
    }
    if (conversationId && conversationId === created.current) {
      created.current = null;
      setLoadedId(conversationId);
      return;
    }
    reset();
    if (!conversationId) return;
    let live = true;
    const following = new AbortController();
    setLoading(true);
    api
      .conversation(conversationId)
      .then((d) => {
        if (!live) return;
        setLoadedId(d.id);
        setMessages(d.messages);
        setContext(d.context);
        // A thread that ends in a question was left mid-answer - reloaded,
        // or the connection dropped. The answer went on without it.
        if (d.messages[d.messages.length - 1]?.role === "user") {
          void follow(d.id, following.signal);
        }
      })
      .catch((e) => live && setLoadError(e instanceof Error ? e.message : "Eroare"))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
      following.abort();
    };
  }, [conversationId]);

  /** A tool reports twice, running then done: replace by id. */
  function onLiveStep(step: StreamStep) {
    setLiveSteps((prev) => {
      const next = prev.slice();
      const at = next.findIndex((s) => s.id === step.id);
      if (at === -1) next.push(step);
      else next[at] = step;
      return next;
    });
  }

  /** Watch the answer still being written in `id`, from its first step. */
  async function follow(id: string, signal: AbortSignal) {
    setBusy(true);
    setLiveSteps([]);
    try {
      const res = await api.resume(id, onLiveStep, signal);
      if (signal.aborted) return;
      setMessages((m) => [
        ...m,
        res
          ? res.message
          : failed(
              "Răspunsul la această întrebare nu a fost finalizat (de exemplu, " +
                "serviciul a fost repornit). Puneți întrebarea din nou.",
            ),
      ]);
      if (res) setContext(res.context);
    } catch (err) {
      if (signal.aborted) return;
      setMessages((m) => [...m, failed(err)]);
    } finally {
      setBusy(false);
      setLiveSteps([]);
    }
  }

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  function openSource(c: Citation) {
    setDraftId(null);
    // Clicking the open source again closes it.
    setSource((s) => (s?.chunk_id === c.chunk_id ? null : c));
  }

  function openDraft(id: string) {
    setSource(null);
    setDraftId((d) => (d === id ? null : id));
  }

  /**
   * A provision opened from a template field, which has a path but no
   * citation. `snippet` is empty on purpose: it is the panel's fallback for
   * highlighting, and inventing text would highlight the wrong passage.
   */
  function openProvision(docId: string, path: string) {
    setDraftId(null);
    setSource({
      chunk_id: `provision:${docId}:${path}`,
      doc_id: docId,
      family_id: "",
      title: "",
      label: "Temei legal",
      path,
      effective_from: null,
      effective_to: null,
      is_current: true,
      snippet: "",
    });
  }

  function afterDelete(id: string) {
    setConversations((cs) => cs.filter((c) => c.id !== id));
    if (id === loadedId) navigate("/chat");
  }

  async function ask(question: string) {
    const q = question.trim();
    if (!q || busy) return;

    // Only files the server could read are sent. A failed chip stays until
    // cleared, but referring to it would be referring to nothing.
    const sending = attached.filter((a) => a.status === "ready");

    setMessages((m) => [
      ...m,
      {
        id: `local-${Date.now()}`,
        role: "user",
        content: q,
        attachments: sending,
        created_at: new Date().toISOString(),
      },
    ]);
    setDraft("");
    setAttached([]);
    setUploadError(null);
    setBusy(true);
    setLiveSteps([]);

    // A new thread takes its URL as soon as the server names it, so a
    // reload mid-answer comes back to it rather than to an empty screen.
    let thread = loadedId;
    const place = (id: string) => {
      if (thread) return;
      thread = id;
      created.current = id;
      setLoadedId(id);
      navigate(`/chat/${id}`);
      void refreshList();
    };

    try {
      const res = await api.askStream(
        q, loadedId, onLiveStep, sending.map((a) => a.id), place,
      );
      // Moved to another thread meanwhile: the answer is stored in its own.
      if (thread && shown.current !== thread) return;
      place(res.conversation_id); // an API too old to send `started`
      setMessages((m) => [...m, res.message]);
      setContext(res.context);
      // A document the user asked for opens without being clicked: the
      // request was "make me a contract", not "make me a card to find".
      const made = res.message.drafts;
      if (made && made.length > 0) {
        setSource(null);
        setDraftId(made[made.length - 1].id);
      }
    } catch (err) {
      if (thread && shown.current !== thread) return;
      setMessages((m) => [...m, failed(err)]);
    } finally {
      setBusy(false);
      setLiveSteps([]);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void ask(draft);
    }
  }

  const full = context ? context.used_tokens >= context.max_tokens : false;
  const title =
    conversations.find((c) => c.id === loadedId)?.title ??
    (messages[0]?.role === "user" ? messages[0].content : "Conversație nouă");

  const list = (
    <ConversationList
      conversations={conversations}
      activeId={loadedId}
      onNew={() => navigate("/chat")}
      onDeleted={afterDelete}
      onPicked={() => setDrawer(false)}
    />
  );

  return (
    <Shell user={user} section="chat" secondary={list} onLogout={onLogout}>
      <section className="chat" aria-label="Conversație">
        <div className="bar">
          <button
            className="iconb only-mobile"
            onClick={() => setDrawer((d) => !d)}
            aria-expanded={drawer}
            aria-label="Conversații"
          >
            <IconMenu />
          </button>
          <span className="title">{title}</span>
          {/* The way back to a document after its panel was closed, without
              scrolling up to the answer that made it. */}
          {threadDocs.length > 0 && (
            <div className="doc-reopen export">
              <button
                className={draftId ? "ghost on" : "ghost"}
                aria-expanded={docMenu}
                onClick={() => {
                  if (draftId) setDraftId(null);
                  else if (threadDocs.length === 1) openDraft(threadDocs[0].id);
                  else setDocMenu((o) => !o);
                }}
              >
                <IconDoc size={16} />
                {draftId ? "Închide documentul" : threadDocs.length === 1 ? "Document" : `Documente (${threadDocs.length})`}
              </button>
              {docMenu && !draftId && (
                <div className="export-menu" role="menu">
                  {threadDocs.map((d) => (
                    <button
                      key={d.id}
                      role="menuitem"
                      className="picker-item"
                      onClick={() => {
                        setDocMenu(false);
                        openDraft(d.id);
                      }}
                    >
                      <span>{d.title}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <button
            className="ghost"
            disabled={!messages.some((m) => m.role === "assistant" && !m.error)}
            onClick={() =>
              saveBlob(
                new Blob([threadMarkdown(title, messages)], { type: "text/markdown" }),
                "conversatie-arhilex.md",
              )
            }
          >
            Exportă
          </button>
        </div>

        {drawer && <div className="drawer">{list}</div>}

        <div className="thread">
          <div className="col">
            {loading && <p className="muted">se încarcă…</p>}
            {loadError && <p className="error" role="alert">{loadError}</p>}

            {!loading && !loadError && messages.length === 0 && conversationId === loadedId && (
              <div className="chat-empty">
                <h2>Ce doriți să verificați?</h2>
                <p className="muted">
                  Întrebați despre o normă din legislația Republicii Moldova.
                  Răspunsul va cita articolele pe care se bazează.
                </p>
                <div className="examples">
                  {EXAMPLES.map((e) => (
                    <button key={e} className="example" onClick={() => void ask(e)}>
                      {e}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m) =>
              m.role === "user" ? (
                <div className="q" key={m.id}>
                  {m.content}
                  <AttachmentList items={m.attachments ?? []} />
                </div>
              ) : (
                <AnswerCard
                  key={m.id}
                  message={m}
                  activeSource={source?.chunk_id ?? null}
                  activeDraft={draftId}
                  onOpenDocument={openSource}
                  onOpenDraft={openDraft}
                />
              ),
            )}

            {busy && <LiveSteps steps={liveSteps} />}
            <div ref={endRef} />
          </div>
        </div>

        {/* Replaying a long thread costs more per answer without improving
            it, so the limit is surfaced as advice first and a block second. */}
        {context?.should_start_new && (
          <div className={full ? "ctx-banner full" : "ctx-banner"}>
            {full ? (
              <>Această conversație a atins limita. Începeți una nouă pentru a continua.</>
            ) : (
              <>
                Conversație lungă ({Math.round(context.used_tokens / 1000)}k din{" "}
                {Math.round(context.max_tokens / 1000)}k). Pentru o întrebare
                nouă, o conversație nouă e mai rapidă și mai ieftină.
              </>
            )}
            <button className="linkbtn" onClick={() => navigate("/chat")}>
              Conversație nouă
            </button>
          </div>
        )}

        <form
          className="composer"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            void ask(draft);
          }}
        >
          <div className="box">
            <AttachmentChips
              attached={attached}
              uploading={uploads.uploading}
              onRemove={(id) => void uploads.remove(id)}
            />
            <label htmlFor="ask" className="sr-only">Întrebarea dumneavoastră</label>
            <textarea
              id="ask"
              rows={2}
              placeholder={full ? "Începeți o conversație nouă" : "Întrebați despre o lege, un contract, o situație…"}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={busy || full}
            />
            <div className="box-row">
              {uploads.input}
              <AttachButton
                onClick={uploads.pick}
                disabled={busy || full || uploads.busy}
              />
              <button
                type="submit"
                className="send"
                aria-label="Trimite"
                disabled={busy || full || !draft.trim()}
              >
                <IconSend />
              </button>
            </div>
          </div>
          {uploadError && <p className="error attach-error" role="alert">{uploadError}</p>}
          <p className="hint">
            Răspunsurile citează legea în vigoare. Verificați sursa înainte de a
            vă baza pe ele.
          </p>
          {/* Permanent, not only on the consent screen: the risk recurs with
              every question, and with every file. */}
          <p className="hint warn">
            Anonimizați documentele înainte de a le încărca — secretul
            profesional aparține clientului. Conversațiile sunt înregistrate.
          </p>
        </form>
      </section>

      {draftId && (
        // Keyed on the id: the load effect runs on mount, and reusing the
        // instance would show the previous document while the next arrives.
        <DraftEditor
          key={draftId}
          draftId={draftId}
          variant="panel"
          onBack={() => setDraftId(null)}
          onOpenSource={openProvision}
        />
      )}

      {source && source.kind === "decision" && (
        <DecisionPanel
          key={source.chunk_id}
          citation={source}
          onClose={() => setSource(null)}
        />
      )}

      {source && source.kind !== "decision" && (
        // Keyed on the chunk so a different citation remounts the panel and
        // its scroll-to-target runs again.
        <DocumentPanel
          key={source.chunk_id}
          citation={source}
          onClose={() => setSource(null)}
        />
      )}
    </Shell>
  );
}
